import type { AppState } from '../types';
import { validateAppState } from '../store';

/** Клиент REST API сервера (SQLite). Один и тот же origin, что и статика. */

/**
 * Всё, что приходит с сервера, проходит через клиентский валидатор.
 *
 * Он же выполняет миграции: состояния, записанные до появления справочника
 * валют, несут скаляр eurUsdRate и не имеют currencies. Без этой проверки такой
 * объект попадал прямо в стор, и первый же `state.currencies.map` падал.
 * Непригодное состояние отбрасываем — приложение продолжит на локальном.
 */
function adoptRemote(raw: unknown): AppState | null {
  const result = validateAppState(raw);
  if (result.ok) return result.state;
  console.error('состояние с сервера не прошло проверку:', result.errors);
  return null;
}

export interface StateResponse {
  exists: boolean;
  rev: number;
  updatedAt?: number;
  state: AppState | null;
}

export interface PutOk {
  ok: true;
  rev: number;
  updatedAt: number;
}

export interface PutConflict {
  ok: false;
  kind: 'conflict';
  rev: number;
  state: AppState;
}

export interface PutRejected {
  ok: false;
  /** unauthorized — сессия кончилась: клиент уходит в локальный режим. */
  kind: 'invalid' | 'network' | 'unauthorized';
  errors: string[];
}

export type PutResult = PutOk | PutConflict | PutRejected;

export interface SnapshotMeta {
  id: number;
  label: string;
  rev: number;
  createdAt: number;
  size: number;
}

/**
 * Адрес поездки в запросе. Владелец сверяется на сервере по сессии, поэтому
 * подстановка чужого id даёт 404, а не доступ к чужим данным. Без параметра
 * сервер открывает поездку по умолчанию — последнюю, которую правили.
 */
function withTrip(path: string, tripId?: string | null): string {
  return tripId ? `${path}${path.includes('?') ? '&' : '?'}trip=${encodeURIComponent(tripId)}` : path;
}

export async function fetchState(tripId?: string | null): Promise<StateResponse | null> {
  try {
    const res = await fetch(withTrip('/api/state', tripId), {
      headers: { accept: 'application/json' },
    });
    if (!res.ok) return null;
    const body = (await res.json()) as StateResponse;
    if (!body.exists || body.state === null) return { ...body, state: null };
    const state = adoptRemote(body.state);
    // Состояние есть, но негодное — ведём себя так же, как если бы его не было.
    return state ? { ...body, state } : { ...body, exists: false, state: null };
  } catch {
    return null; // сервера нет — работаем локально
  }
}

export async function putState(
  state: AppState,
  rev: number,
  tripId?: string | null,
): Promise<PutResult> {
  try {
    const res = await fetch(withTrip('/api/state', tripId), {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ rev, state }),
    });

    if (res.ok) return (await res.json()) as PutOk;

    if (res.status === 401) {
      return { ok: false, kind: 'unauthorized', errors: ['сессия завершена'] };
    }

    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      errors?: string[];
      rev?: number;
      state?: AppState;
    };

    if (res.status === 409 && body.state) {
      const state = adoptRemote(body.state);
      if (state) return { ok: false, kind: 'conflict', rev: body.rev ?? 0, state };
      return { ok: false, kind: 'invalid', errors: ['версия сервера не прошла проверку'] };
    }
    return {
      ok: false,
      kind: 'invalid',
      errors: body.errors ?? [body.error ?? `сервер ответил ${res.status}`],
    };
  } catch (err) {
    return { ok: false, kind: 'network', errors: [(err as Error).message] };
  }
}

export async function listSnapshots(tripId?: string | null): Promise<SnapshotMeta[]> {
  try {
    const res = await fetch(withTrip('/api/snapshots', tripId));
    if (!res.ok) return [];
    const body = (await res.json()) as { snapshots: SnapshotMeta[] };
    return body.snapshots;
  } catch {
    return [];
  }
}

/**
 * Снимок текущего состояния. `state` передаётся только в одном случае: гость
 * входит в аккаунт, где на сервере уже что-то есть, и его локальную работу
 * надо сохранить прежде, чем применить серверную версию.
 */
export async function createSnapshot(
  label: string,
  state?: AppState,
  tripId?: string | null,
): Promise<boolean> {
  try {
    const res = await fetch(withTrip('/api/snapshots', tripId), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(state === undefined ? { label } : { label, state }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function fetchSnapshot(id: number): Promise<AppState | null> {
  try {
    const res = await fetch(`/api/snapshots/${id}`);
    if (!res.ok) return null;
    const body = (await res.json()) as { state: unknown };
    return adoptRemote(body.state);
  } catch {
    return null;
  }
}
